import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '@/api/client/ApiClient';

import { showError500OrUnknownToast } from '@/store/common/showError500OrUnknownToast';
import { AppDispatch, RootState } from '@/store/store.ts';
import { isAxiosError } from 'axios';
import { getErrors } from '@/store/common/error-handlers.ts';
import { CodeType } from '@/api/__generated__/webApi';
import { getRecaptchaToken } from '@/hooks/useRecaptcha.tsx';
import { ValidationErrorType } from '@/types/ValidationError.ts';
import { successToast } from '@/components/Toast';
import { getTranslate } from '@/i18n/getTranslate.ts';
import { contactVivicaSupportToast } from '@/components/Toast/contactVivicaSupportToast.tsx';

/* fetch list --------------------------------------------------------- */
export const fetchSessions = createAsyncThunk(
  'sessions/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await client.getAllActiveSessions();
      return data;
    } catch (err) {
      showError500OrUnknownToast(err);
      return rejectWithValue(err);
    }
  }
);
export const sendRemoveRequest = createAsyncThunk<
  number | undefined, //   ▲ return resendTime
  undefined,
  { state: RootState; rejectValue: string }
>('sessions/sendRemove', async (_, { getState, rejectWithValue }) => {
  const { targetIds } = getState().sessions;
  if (!targetIds) {
    console.error('[sendRemoveRequest] No targetIds selected.', targetIds);
    return;
  }
  try {
    const {
      data: { resendTime },
    } = await client.removeSessions({ sessionIds: targetIds });
    return resendTime; // ← payload for fulfilled
  } catch {
    await contactVivicaSupportToast();
    return rejectWithValue('Failed to send SMS.');
  }
});

interface PinRejectPayload {
  pinError?: ValidationErrorType;
  error?: unknown;
}

/*  /confirm/remove ---------------------------------------------------- */
export const confirmRemove = createAsyncThunk<
  undefined,
  { code: string },
  { state: RootState; dispatch: AppDispatch; rejectValue: PinRejectPayload }
>(
  'sessions/confirm',
  async ({ code }, { dispatch, getState, rejectWithValue }) => {
    try {
      const recaptcha = await getRecaptchaToken();
      const sessionsAmount = getState().sessions.targetIds?.length ?? 0;
      await client.confirmRemoveSessions({ code, recaptcha });
      await dispatch(fetchSessions());
      const t = await getTranslate('profile');
      void successToast({
        title: t('SUCCESS_NO_EXCL'),
        description: t('SESSION_CLOSED_PLURAL', {
          count: sessionsAmount,
        }),
      });
    } catch (err) {
      /* 400 → ValidationError */
      if (
        isAxiosError(err) &&
        err.response?.status === 400 &&
        err.response.data
      ) {
        const pinFieldErr = getErrors(err);
        return rejectWithValue({
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          pinError: pinFieldErr.code ?? pinFieldErr.common,
        });
      }

      /* 500 / network / unknown */
      await contactVivicaSupportToast();
      return rejectWithValue({ error: ValidationErrorType.UNKNOWN_ERROR });
    }
  }
);
/* Re-send PIN ------------------------------------------------------------- */
export const resendPinCode = createAsyncThunk<
  number | undefined, //   ▲ return resendTime
  undefined,
  { rejectValue: string }
>('sessions/resendPin', async (_, { rejectWithValue }) => {
  try {
    const recaptcha = await getRecaptchaToken();
    const {
      data: { resendTime },
    } = await client.sendCode({
      codeType: CodeType.REMOVE_SESSIONS,
      recaptcha,
    });
    return resendTime; // ← payload for fulfilled
  } catch (raw) {
    showError500OrUnknownToast(raw);
    return rejectWithValue('Failed to resend code. Try again.');
  }
});
